<template>
    <v-container>
        <h2 class="mb-4">{{ mode === 'create' ? 'Create Sport' : mode === 'edit' ? 'Edit Sport' : 'View Sport' }}</h2>
        <div v-if="mode && !['edit', 'create'].includes(mode)" class="edit-button-wrapper">
            <v-btn class="mx-2" dark medium color="primary" :loading="navigating" :disabled="navigating || submitting || deletingImage || deletingIconImage" @click="onEdit">
                <v-icon dark>mdi-account-edit</v-icon>
            </v-btn>
        </div>
        <v-form :readonly="readOnly">
            <v-text-field
                v-model="sport.title"
                :rules="rules"
                label="Title"
                :clearable="clearable"
            />
            <v-select
                v-model="sport.type"
                :items="typeOptions"
                label="Type"
                :clearable="clearable"
            />
            <v-file-input
                v-if="mode !== 'show'"
                v-model="newImage"
                label="Sport Image"
                :rules="imageRules"
                filled
                show-size
                prepend-icon="mdi-camera"
                @change="createNewImagePreview"
            />
            <div v-if="previewImage && !previewImage.shouldDelete" class="image-input" :class="{'green-border': !previewImage.id}">
                <div v-if="mode === 'edit'" class="delete-btn">
                    <v-btn class="mx-2 white--text" small color="secondary" :loading="deletingImage" :disabled="deletingImage || deletingIconImage || submitting || navigating" @click="onImageDelete">
                        X
                    </v-btn>
                </div>
                <v-img v-if="previewImage.url" class="img-preview" :src="previewImage.url" contain height="180" />
            </div>
            <v-file-input
                v-if="mode !== 'show'"
                v-model="newIconImage"
                label="Sport Icon"
                :rules="imageRules"
                filled
                show-size
                prepend-icon="mdi-image-filter-center-focus"
                @change="createNewIconImagePreview"
            />
            <div v-if="previewIconImage && !previewIconImage.shouldDelete" class="image-input" :class="{'green-border': !previewIconImage.id}">
                <div v-if="mode === 'edit'" class="delete-btn">
                    <v-btn class="mx-2 white--text" small color="secondary" :loading="deletingIconImage" :disabled="deletingIconImage || deletingImage || submitting || navigating" @click="onIconImageDelete">
                        X
                    </v-btn>
                </div>
                <v-img v-if="previewIconImage.url" class="img-preview" :src="previewIconImage.url" contain height="180" />
            </div>
        </v-form>
        <div v-if="mode !== 'show'" class="submit-button-wrapper">
            <v-btn class="mx-2 white--text" :loading="submitting" :disabled="shouldDisableSubmit() || submitting || navigating || deletingImage || deletingIconImage" large color="primary" @click="onSubmit">
                Submit
            </v-btn>
        </div>
    </v-container>
</template>

<script>
    import imageService from '@/services/image-service'
    import sportService from '@/services/sport-service'

    export default {
        name: 'SportForm',
        data: () => {
            return {
                mode: null,
                readOnly: false,
                clearable: true,
                apiUrl: process.env.VUE_APP_API_HOST_DOMAIN,
                sport: {
                    title: '',
                    type: ''
                },
                typeOptions: ['sport', 'academy'],
                sportId: null,
                rules: [
                    v => !!v || 'Name is required'
                ],
                newImage: null,
                previewImage: null,
                newIconImage: null,
                previewIconImage: null,
                imageRules: [
                    value => !value || value.size < 2000000 || 'Image size should be less than 2 MB!'
                ],
                submitting: false,
                navigating: false,
                deletingImage: false,
                deletingIconImage: false
            }
        },
        created() {
            this.mode = this.$route.params.mode
            if (this.mode === 'create') {
                return
            } else if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
            }
            this.sportId = this.$route.params.sportId
            if (this.sportId) {
                sportService.getSportById(this.sportId).then(resp => {
                    this.sport = resp.data.sport
                    this.previewImage = this.sport.image
                    if (this.previewImage) {
                        this.previewImage.url = process.env.VUE_APP_GOOGLE_BUCKET_URL + this.previewImage.imageName
                    }
                    this.previewIconImage = this.sport.iconImage || this.sport.icon_image
                    if (this.previewIconImage) {
                        this.previewIconImage.url = process.env.VUE_APP_GOOGLE_BUCKET_URL + this.previewIconImage.imageName
                    }
                })
            }
        },
        methods: {
            shouldDisableSubmit() {
                return !this.sport.title || !this.sport.type
            },
            async onEdit() {
                this.navigating = true
                try {
                    await this.$router.push(`/sports/${this.sportId}/edit`)
                } finally {
                    this.navigating = false
                }
            },
            async createSport() {
                const response = await sportService.createSport(this.buildSportFormData())
                if (response.data.id) {
                    this.sportId = response.data.id
                }
                this.$router.push(`/sports`)
            },
            async editSport() {
                await sportService.editSportById(this.sportId, this.buildSportFormData())
                this.$router.push(`/sports`)
            },
            async onSubmit() {
                this.submitting = true
                try {
                    if (this.mode === 'edit') {
                        await this.editSport()
                    } else if (this.mode === 'create') {
                        await this.createSport()
                    }
                    await this.deleteSportImage()
                    await this.deleteSportIconImage()
                } finally {
                    this.submitting = false
                }
            },
            async deleteSportImage() {
                if (this.previewImage && this.previewImage.id && this.previewImage.shouldDelete) {
                    await imageService.deleteImageById(this.previewImage.id)
                    this.previewImage = null
                }
            },
            async deleteSportIconImage() {
                if (this.previewIconImage && this.previewIconImage.id && this.previewIconImage.shouldDelete) {
                    await imageService.deleteImageById(this.previewIconImage.id)
                    this.previewIconImage = null
                }
            },
            createNewImagePreview() {
                if (!this.hasSelectedImage(this.newImage)) {
                    this.previewImage = null
                    return
                }
                this.previewImage = this.newImage
                this.previewImage.url = URL.createObjectURL(this.newImage)
            },
            createNewIconImagePreview() {
                if (!this.hasSelectedImage(this.newIconImage)) {
                    this.previewIconImage = null
                    return
                }
                this.previewIconImage = this.newIconImage
                this.previewIconImage.url = URL.createObjectURL(this.newIconImage)
            },
            buildSportFormData() {
                const formData = new FormData()
                formData.append('title', this.sport.title)
                if (this.sport.type) {
                    formData.append('type', this.sport.type)
                }

                if (this.hasSelectedImage(this.newImage)) {
                    formData.append('file', this.newImage)
                }
                if (this.hasSelectedImage(this.newIconImage)) {
                    formData.append('iconFile', this.newIconImage)
                }

                return formData
            },
            onImageDelete() {
                this.deletingImage = true
                if (this.previewImage && this.previewImage.id) {
                    this.previewImage.shouldDelete = true
                    this.previewImage.url = null
                    this.previewImage = { ...this.previewImage }
                } else {
                    this.newImage = null
                }
                this.deletingImage = false
            },
            onIconImageDelete() {
                this.deletingIconImage = true
                if (this.previewIconImage && this.previewIconImage.id) {
                    this.previewIconImage.shouldDelete = true
                    this.previewIconImage.url = null
                    this.previewIconImage = { ...this.previewIconImage }
                } else {
                    this.newIconImage = null
                }
                this.deletingIconImage = false
            },
            hasSelectedImage(image) {
                return image && typeof image === 'object' && image.size !== undefined && image.name
            }
        }
    }
</script>

<style scoped>
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.image-input {
    border: 2px dashed #ccc;
    padding: 20px;
    border-radius: 4px;
    margin-top: 20px;
    position: relative;
}

.image-input.green-border {
    border-color: #4caf50;
}

.delete-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
}

.img-preview {
    width: 100%;
    max-width: 100%;
    height: 180px;
    margin: 0 auto 10px;
}
</style>


